import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Truck, Calculator, FileText, BarChart3, Shield, Users, Brain, Target,
  ArrowRight, Mail, Phone, MapPin, ChevronRight, Globe, Heart,
  CheckCircle2, Zap, Building2, GraduationCap, Upload, Linkedin, ExternalLink
} from 'lucide-react';

const MODULES = [
  { icon: FileText, title: 'Billing & LR Management', desc: 'Generate GST-compliant freight invoices and loading receipts with auto calculations, consignor/consignee tracking, and multi-format PDF export.' },
  { icon: Calculator, title: 'Accounting System', desc: 'Full double-entry bookkeeping with journal vouchers, trial balance, profit & loss, and balance sheet — matching traditional accounting standards.' },
  { icon: Truck, title: 'Fleet Operations', desc: 'Track vehicles, drivers, routes, maintenance schedules, fuel logs, permits, and insurance — all in one integrated dashboard.' },
  { icon: BarChart3, title: 'Financial Analytics', desc: 'Real-time insights into revenue trends, client profitability, route performance, and expense breakdowns with visual dashboards.' },
  { icon: Brain, title: 'AI Assistant', desc: 'Gemini-powered AI that analyzes your financial data, generates journal entries, detects errors, and provides smart insights — turning data into decisions.' },
  { icon: GraduationCap, title: 'Training Center', desc: 'Built-in training modules with video tutorials and PDF guides to help your team learn the platform quickly.' },
];

const WHY_CHOOSE = [
  { icon: Zap, title: 'Automation', desc: 'Automate billing, GST calculations, and report generation — eliminate manual work.' },
  { icon: Target, title: 'Accuracy', desc: 'Double-entry accounting engine ensures every transaction is balanced and audit-ready.' },
  { icon: BarChart3, title: 'Real-Time Visibility', desc: 'Instant access to P&L, balance sheets, and analytics from any device.' },
  { icon: Brain, title: 'AI-Powered Insights', desc: 'AI assistant analyzes your books, detects anomalies, and suggests optimizations.' },
  { icon: Building2, title: 'Multi-Company Support', desc: 'Manage multiple companies with complete data isolation and separate ledgers.' },
  { icon: Shield, title: 'Security & Compliance', desc: 'GST-compliant, role-based access, audit trails, and encrypted data storage.' },
];

import { FlutterFoxFooter } from '../components/FlutterFoxFooter';

export default function AboutUs() {
  const navigate = useNavigate();

  return (
    <div className="bg-slate-950 text-white min-h-screen" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Nav */}
      <nav className="bg-slate-950/95 backdrop-blur-md border-b border-slate-800/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <a href="/" className="flex items-center gap-3">
            <img src="/images/transsolution_logo.png" alt="Transsolution" className="w-8 h-8 rounded object-contain bg-white p-0.5" />
            <span className="font-bold text-white text-sm tracking-tight" style={{ fontFamily: 'Barlow, sans-serif' }}>TRANSSOLUTION</span>
          </a>
          <div className="flex items-center gap-4">
            <a href="/" className="text-sm text-slate-400 hover:text-orange-500 transition-colors">Home</a>
            <a href="/contact" className="text-sm text-slate-400 hover:text-orange-500 transition-colors">Contact</a>
            <button onClick={() => navigate('/login')} className="text-sm text-slate-300 hover:text-white px-4 py-2">Login</button>
            <a href="/#demo" className="bg-orange-600 hover:bg-orange-500 text-white text-sm font-bold px-5 py-2.5 tracking-wide" style={{ fontFamily: 'Barlow, sans-serif' }}>BOOK DEMO</a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-20 md:py-28" data-testid="about-hero">
        <div className="max-w-5xl mx-auto px-4 md:px-6 text-center">
          <p className="text-orange-500 text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ fontFamily: 'Barlow, sans-serif' }}>About Transsolution</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-6" style={{ fontFamily: 'Barlow, sans-serif' }}>
            CLOUD ERP BUILT FOR LOGISTICS & TRANSPORTATION
          </h1>
          <p className="text-lg text-slate-400 leading-relaxed max-w-3xl mx-auto">
            Transsolution is a cloud-based Enterprise Resource Planning platform designed specifically for logistics,
            freight, and transportation companies. We combine powerful accounting, fleet management, and AI-driven
            analytics into one seamless platform.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-slate-900/30" data-testid="about-mission">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-orange-500 text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ fontFamily: 'Barlow, sans-serif' }}>Our Mission</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Barlow, sans-serif' }}>
                SIMPLIFY FINANCIAL MANAGEMENT FOR LOGISTICS COMPANIES
              </h2>
              <p className="text-slate-400 leading-relaxed mb-6">
                Our mission is to simplify financial and operational management for logistics companies through
                intelligent automation and cloud technology. We believe every transport business — from a single-truck
                operator to a multi-branch fleet — deserves enterprise-grade tools that are easy to use.
              </p>
              <p className="text-slate-400 leading-relaxed">
                Transsolution eliminates the chaos of spreadsheets, disconnected systems, and manual processes
                by providing one unified platform that handles everything from LR generation to balance sheets.
              </p>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-orange-600/5 blur-3xl rounded-2xl" />
              <div className="relative bg-slate-900 border border-slate-800 rounded-xl p-8">
                <div className="grid grid-cols-2 gap-6">
                  {[{ num: '500+', label: 'Active Companies' }, { num: '50K+', label: 'Invoices Generated' },
                    { num: '99.9%', label: 'Uptime' }, { num: '24/7', label: 'Support' }].map((s, i) => (
                    <div key={i} className="text-center">
                      <p className="text-2xl font-bold text-orange-500" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{s.num}</p>
                      <p className="text-xs text-slate-500 mt-1">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-20" data-testid="about-modules">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <p className="text-orange-500 text-xs font-bold uppercase tracking-[0.2em] mb-4 text-center" style={{ fontFamily: 'Barlow, sans-serif' }}>What Transsolution Does</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-center mb-14" style={{ fontFamily: 'Barlow, sans-serif' }}>
            COMPREHENSIVE ERP MODULES
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {MODULES.map((m, i) => (
              <div key={i} className="bg-slate-900/50 border border-slate-800/50 hover:border-orange-500/30 transition-all p-6 rounded-xl group">
                <div className="w-12 h-12 rounded-lg bg-orange-600/10 flex items-center justify-center mb-4 group-hover:bg-orange-600/20 transition-colors">
                  <m.icon size={24} className="text-orange-500" />
                </div>
                <h3 className="text-base font-bold text-slate-200 mb-2" style={{ fontFamily: 'Barlow, sans-serif' }}>{m.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-20 bg-slate-900/30" data-testid="about-why">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <p className="text-orange-500 text-xs font-bold uppercase tracking-[0.2em] mb-4 text-center" style={{ fontFamily: 'Barlow, sans-serif' }}>Why Companies Choose Transsolution</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-center mb-14" style={{ fontFamily: 'Barlow, sans-serif' }}>
            BUILT DIFFERENT. BUILT BETTER.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_CHOOSE.map((w, i) => (
              <div key={i} className="flex gap-4 p-5 bg-slate-900 border border-slate-800 rounded-lg hover:border-orange-500/30 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-orange-600/10 flex items-center justify-center shrink-0">
                  <w.icon size={20} className="text-orange-500" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-200 mb-1" style={{ fontFamily: 'Barlow, sans-serif' }}>{w.title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{w.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Parent Company - Flutter Fox */}
      <section className="py-20" data-testid="about-parent-company">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="text-center mb-14">
            <p className="text-orange-500 text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ fontFamily: 'Barlow, sans-serif' }}>Our Parent Company</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Barlow, sans-serif' }}>BUILT BY FLUTTER FOX</h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-slate-400 leading-relaxed mb-6">
                Transsolution is a product of <strong className="text-orange-500">Flutter Fox</strong> — a software development company that builds custom solutions for businesses of all sizes. From AI-powered agents to enterprise ERPs, Flutter Fox delivers cutting-edge technology.
              </p>
              <p className="text-slate-400 leading-relaxed mb-6">
                Flutter Fox specializes in developing <strong className="text-slate-200">AI Receptionists</strong>, <strong className="text-slate-200">intelligent automation agents</strong>, custom cloud applications, and enterprise-grade software platforms. Every product is designed to simplify operations and drive growth.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="https://flutterfox.in" target="_blank" rel="dofollow" className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white font-bold px-6 py-3 tracking-wide transition-all text-sm" style={{ fontFamily: 'Barlow, sans-serif' }} data-testid="flutterfox-website-btn">
                  <Globe size={16} /> VISIT FLUTTER FOX <ExternalLink size={14} />
                </a>
                <a href="https://www.linkedin.com/company/flutterfoxin" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white font-bold px-6 py-3 tracking-wide transition-all text-sm border border-slate-700" style={{ fontFamily: 'Barlow, sans-serif' }} data-testid="flutterfox-linkedin-btn">
                  <Linkedin size={16} /> LINKEDIN
                </a>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { title: 'Custom Software', desc: 'Tailored solutions for every business need' },
                { title: 'AI Agents', desc: 'AI Receptionists & intelligent automation' },
                { title: 'Enterprise ERP', desc: 'Scalable cloud-native ERP platforms' },
                { title: 'Mobile & Web Apps', desc: 'Cross-platform modern applications' },
              ].map((s, i) => (
                <div key={i} className="bg-slate-900 border border-slate-800 hover:border-orange-500/30 transition-all p-5 rounded-xl">
                  <h4 className="text-sm font-bold text-slate-200 mb-1" style={{ fontFamily: 'Barlow, sans-serif' }}>{s.title}</h4>
                  <p className="text-xs text-slate-500">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-20 bg-slate-900/30" data-testid="about-contact">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <p className="text-orange-500 text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ fontFamily: 'Barlow, sans-serif' }}>Get In Touch</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Barlow, sans-serif' }}>
            CONTACT US
          </h2>
          <p className="text-slate-400 mb-10">Have questions? We'd love to hear from you.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            <a href="mailto:info@transsolution.online" className="bg-slate-900 border border-slate-800 hover:border-orange-500/30 transition-all p-6 rounded-lg">
              <Mail size={24} className="text-orange-500 mx-auto mb-3" />
              <p className="text-xs text-slate-500 mb-1">Email Us</p>
              <p className="text-sm text-slate-200 hover:text-orange-500 font-medium">info@transsolution.online</p>
            </a>
            <a href="tel:+918130095013" className="bg-slate-900 border border-slate-800 hover:border-orange-500/30 transition-all p-6 rounded-lg">
              <Phone size={24} className="text-orange-500 mx-auto mb-3" />
              <p className="text-xs text-slate-500 mb-1">Call Us</p>
              <p className="text-sm text-slate-200 hover:text-orange-500 font-medium">+91 81300 95013</p>
            </a>
            <a href="/contact" className="bg-slate-900 border border-slate-800 hover:border-orange-500/30 transition-all p-6 rounded-lg">
              <Globe size={24} className="text-orange-500 mx-auto mb-3" />
              <p className="text-xs text-slate-500 mb-1">Contact Form</p>
              <p className="text-sm text-orange-500 hover:text-orange-400 font-semibold">Send a Message &rarr;</p>
            </a>
          </div>
          <a href="/contact" className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white font-bold px-10 py-4 tracking-wide transition-all" style={{ fontFamily: 'Barlow, sans-serif' }}>
            CONTACT US <ArrowRight size={18} />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/images/transsolution_logo.png" alt="Logo" className="w-6 h-6 rounded object-contain bg-white p-0.5" />
            <span className="text-xs text-slate-500">&copy; 2026 Transsolution ERP. All Rights Reserved.</span>
          </div>
          <FlutterFoxFooter testId="about-footer-flutterfox" />
        </div>
      </footer>
    </div>
  );
}
